import { indexedColor } from './colors';

const DECIMAL = ',';
const GROUP = ' ';
const FILL_MARK = '\u0000';
const GENERAL_LENGTH = 11;
const SIGNIFICANT_DIGITS = 15;
export const LAST_DATE_SERIAL = 2958466;

const BUILTIN_FORMATS: Record<number, string> = {
  0: 'General',
  1: '0',
  2: '0.00',
  3: '#,##0',
  4: '#,##0.00',
  5: '#,##0\\ "₽";\\-#,##0\\ "₽"',
  6: '#,##0\\ "₽";[Red]\\-#,##0\\ "₽"',
  7: '#,##0.00\\ "₽";\\-#,##0.00\\ "₽"',
  8: '#,##0.00\\ "₽";[Red]\\-#,##0.00\\ "₽"',
  9: '0%',
  10: '0.00%',
  11: '0.00E+00',
  12: '# ?/?',
  13: '# ??/??',
  14: 'dd.mm.yyyy',
  15: 'd-mmm-yy',
  16: 'd-mmm',
  17: 'mmm-yy',
  18: 'h:mm AM/PM',
  19: 'h:mm:ss AM/PM',
  20: 'h:mm',
  21: 'h:mm:ss',
  22: 'dd.mm.yyyy h:mm',
  37: '#,##0 ;(#,##0)',
  38: '#,##0 ;[Red](#,##0)',
  39: '#,##0.00;(#,##0.00)',
  40: '#,##0.00;[Red](#,##0.00)',
  41: '_-* #,##0_-;\\-* #,##0_-;_-* "-"_-;_-@_-',
  42: '_-* #,##0\\ "₽"_-;\\-* #,##0\\ "₽"_-;_-* "-"\\ "₽"_-;_-@_-',
  43: '_-* #,##0.00_-;\\-* #,##0.00_-;_-* "-"??_-;_-@_-',
  44: '_-* #,##0.00\\ "₽"_-;\\-* #,##0.00\\ "₽"_-;_-* "-"??\\ "₽"_-;_-@_-',
  45: 'mm:ss',
  46: '[h]:mm:ss',
  47: 'mm:ss.0',
  48: '##0.0E+0',
  49: '@',
};

export function builtinFormat(id: number): string | undefined {
  return BUILTIN_FORMATS[id];
}

const NAMED_COLORS: Record<string, string> = {
  black: '#000000',
  blue: '#0000FF',
  cyan: '#00FFFF',
  green: '#00FF00',
  magenta: '#FF00FF',
  red: '#FF0000',
  white: '#FFFFFF',
  yellow: '#FFFF00',
};

const MONTHS = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
];
const MONTHS_GENITIVE = [
  'января',
  'февраля',
  'марта',
  'апреля',
  'мая',
  'июня',
  'июля',
  'августа',
  'сентября',
  'октября',
  'ноября',
  'декабря',
];
const MONTHS_SHORT = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const DAYS = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
const DAYS_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];
const MONTHS_EN = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_EN = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

type Token =
  | { t: 'lit'; v: string }
  | { t: 'fill'; v: string }
  | { t: 'digit'; v: string }
  | { t: 'point' }
  | { t: 'comma' }
  | { t: 'percent' }
  | { t: 'exp'; plus: boolean }
  | { t: 'slash' }
  | { t: 'text' }
  | { t: 'general' }
  | { t: 'date'; v: string }
  | { t: 'ampm'; v: string }
  | { t: 'elapsed'; v: string }
  | { t: 'subsec'; n: number };

interface Condition {
  op: string;
  value: number;
}

interface Section {
  tokens: Token[];
  color?: string;
  condition?: Condition;
  isDate: boolean;
  hasText: boolean;
  hasDigits: boolean;
  genitive: boolean;
  english: boolean;
}

export interface FormattedValue {
  text: string;
  color?: string;
  fill?: { before: string; after: string };
}

function splitSections(code: string): string[] {
  const sections: string[] = [];
  let current = '';
  for (let i = 0; i < code.length; i++) {
    const char = code[i];
    if (char === '"') {
      const end = code.indexOf('"', i + 1);
      const stop = end < 0 ? code.length : end;
      current += code.slice(i, stop + 1);
      i = stop;
    } else if (char === '\\' || char === '_' || char === '*') {
      current += code.slice(i, i + 2);
      i += 1;
    } else if (char === '[') {
      const end = code.indexOf(']', i);
      const stop = end < 0 ? code.length : end;
      current += code.slice(i, stop + 1);
      i = stop;
    } else if (char === ';') {
      sections.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  sections.push(current);
  return sections;
}

function readBracket(content: string, section: Section, tokens: Token[]): void {
  const lower = content.toLowerCase();
  if (NAMED_COLORS[lower]) {
    section.color = NAMED_COLORS[lower];
    return;
  }
  const colorIndex = lower.match(/^color\s*(\d+)$/);
  if (colorIndex) {
    section.color = indexedColor(Number(colorIndex[1]) + 7);
    return;
  }
  const condition = content.match(/^(<=|>=|<>|<|>|=)\s*(-?\d+(?:\.\d+)?)$/);
  if (condition) {
    section.condition = { op: condition[1], value: Number(condition[2]) };
    return;
  }
  if (content.startsWith('$')) {
    const [symbol, locale = ''] = content.slice(1).split('-');
    if (symbol) {
      tokens.push({ t: 'lit', v: symbol });
    }
    if (/^f[0-9a-f]19$/i.test(locale)) {
      section.genitive = true;
    }
    if ((parseInt(locale, 16) & 0xff) === 0x09) {
      section.english = true;
    }
    return;
  }
  if (/^(h+|m+|s+)$/i.test(content)) {
    tokens.push({ t: 'elapsed', v: lower[0] });
  }
}

function tokenize(source: string): Section {
  const section: Section = {
    tokens: [],
    isDate: false,
    hasText: false,
    hasDigits: false,
    genitive: false,
    english: false,
  };
  const tokens = section.tokens;
  let i = 0;
  while (i < source.length) {
    const char = source[i];
    const lower = char.toLowerCase();
    const rest = source.slice(i).toLowerCase();

    if (char === '"') {
      const end = source.indexOf('"', i + 1);
      const stop = end < 0 ? source.length : end;
      tokens.push({ t: 'lit', v: source.slice(i + 1, stop) });
      i = stop + 1;
    } else if (char === '\\') {
      tokens.push({ t: 'lit', v: source[i + 1] ?? '' });
      i += 2;
    } else if (char === '_') {
      tokens.push({ t: 'lit', v: ' ' });
      i += 2;
    } else if (char === '*') {
      tokens.push({ t: 'fill', v: source[i + 1] ?? ' ' });
      i += 2;
    } else if (char === '[') {
      const end = source.indexOf(']', i);
      const stop = end < 0 ? source.length : end;
      readBracket(source.slice(i + 1, stop), section, tokens);
      i = stop + 1;
    } else if (rest.startsWith('general')) {
      tokens.push({ t: 'general' });
      i += 7;
    } else if (rest.startsWith('am/pm')) {
      tokens.push({ t: 'ampm', v: 'AM/PM' });
      i += 5;
    } else if (rest.startsWith('a/p')) {
      tokens.push({ t: 'ampm', v: 'A/P' });
      i += 3;
    } else if (char === '0' || char === '#' || char === '?') {
      tokens.push({ t: 'digit', v: char });
      i += 1;
    } else if (char === '.') {
      const previous = [...tokens].reverse().find((token) => token.t !== 'lit');
      const zeros = source.slice(i + 1).match(/^0+/)?.[0].length ?? 0;
      if (zeros && previous && (previous.t === 'date' || previous.t === 'elapsed') && /^s/.test(previous.v)) {
        tokens.push({ t: 'subsec', n: Math.min(zeros, 3) });
        i += 1 + zeros;
      } else {
        tokens.push({ t: 'point' });
        i += 1;
      }
    } else if (char === ',') {
      tokens.push({ t: 'comma' });
      i += 1;
    } else if (char === '%') {
      tokens.push({ t: 'percent' });
      i += 1;
    } else if ((char === 'E' || char === 'e') && (source[i + 1] === '+' || source[i + 1] === '-')) {
      tokens.push({ t: 'exp', plus: source[i + 1] === '+' });
      i += 2;
    } else if (char === '/') {
      tokens.push({ t: 'slash' });
      i += 1;
    } else if (char === '@') {
      tokens.push({ t: 'text' });
      i += 1;
    } else if ('ymdhse'.includes(lower)) {
      let end = i;
      while (end < source.length && source[end].toLowerCase() === lower) {
        end++;
      }
      tokens.push({ t: 'date', v: (lower === 'e' ? 'y' : lower).repeat(end - i) });
      i = end;
    } else {
      tokens.push({ t: 'lit', v: char });
      i += 1;
    }
  }

  section.isDate = tokens.some((token) => token.t === 'date' || token.t === 'elapsed' || token.t === 'ampm');
  section.hasText = tokens.some((token) => token.t === 'text');
  section.hasDigits = tokens.some((token) => token.t === 'digit' || token.t === 'general');

  if (section.isDate) {
    const dateTokens = tokens.filter(
      (token): token is Extract<Token, { t: 'date' | 'elapsed' }> => token.t === 'date' || token.t === 'elapsed',
    );
    dateTokens.forEach((token, index) => {
      if (token.t !== 'date' || token.v[0] !== 'm' || token.v.length > 2) {
        return;
      }
      const previous = dateTokens[index - 1];
      const next = dateTokens[index + 1];
      if ((previous && previous.v[0] === 'h') || (next && next.v[0] === 's')) {
        token.v = token.v.replace(/m/g, 'n');
      }
    });
  }
  return section;
}

const compiledCache = new Map<string, Section[]>();

function compile(code: string): Section[] {
  let sections = compiledCache.get(code);
  if (!sections) {
    sections = splitSections(code).map(tokenize);
    compiledCache.set(code, sections);
  }
  return sections;
}

export function isDateFormat(code?: string): boolean {
  if (!code) {
    return false;
  }
  return compile(code)[0]?.isDate ?? false;
}

function testCondition(condition: Condition, value: number): boolean {
  switch (condition.op) {
    case '<':
      return value < condition.value;
    case '<=':
      return value <= condition.value;
    case '>':
      return value > condition.value;
    case '>=':
      return value >= condition.value;
    case '=':
      return value === condition.value;
    case '<>':
      return value !== condition.value;
  }
  return false;
}

function pickSection(sections: Section[], value: number): { section: Section; signed: boolean } {
  const numeric = sections.slice(0, 3);
  if (numeric[0].condition) {
    if (testCondition(numeric[0].condition, value)) {
      return { section: numeric[0], signed: value < 0 };
    }
    if (numeric[1]?.condition) {
      if (testCondition(numeric[1].condition, value)) {
        return { section: numeric[1], signed: value < 0 };
      }
      return { section: numeric[2] ?? numeric[1], signed: !numeric[2] && value < 0 };
    }
    return { section: numeric[1] ?? numeric[0], signed: false };
  }
  if (numeric.length === 1 || value > 0) {
    return { section: numeric[0], signed: numeric.length === 1 && value < 0 };
  }
  if (value < 0) {
    return { section: numeric[1], signed: false };
  }
  return { section: numeric[2] ?? numeric[0], signed: false };
}

function trimFraction(text: string): string {
  return text.includes('.') ? text.replace(/\.?0+$/, '') : text;
}

function fixedDecimal(value: number, decimals: number): string {
  if (!Number.isFinite(value)) {
    return value.toFixed(decimals);
  }
  const [mantissa, exponent] = value.toExponential(SIGNIFICANT_DIGITS - 1).split('e');
  let digits = mantissa.replace('.', '');
  let integerLength = Number(exponent) + 1;
  if (integerLength < 1) {
    digits = '0'.repeat(1 - integerLength) + digits;
    integerLength = 1;
  }
  const keep = integerLength + decimals;
  digits = digits.padEnd(keep + 1, '0');
  const kept = digits.slice(0, keep).split('');
  if (digits[keep] >= '5') {
    let index = kept.length - 1;
    while (index >= 0 && kept[index] === '9') {
      kept[index--] = '0';
    }
    if (index < 0) {
      kept.unshift('1');
      integerLength++;
    } else {
      kept[index] = String(Number(kept[index]) + 1);
    }
  }
  const integer = kept
    .slice(0, integerLength)
    .join('')
    .replace(/^0+(?=\d)/, '');
  return decimals ? `${integer}.${kept.slice(integerLength).join('')}` : integer;
}

function exponentText(value: number, decimals: number): string {
  const [mantissa, exponent] = value.toExponential(decimals).split('e');
  const power = Number(exponent);
  const sign = power < 0 ? '-' : '+';
  return `${trimFraction(mantissa).replace('.', DECIMAL)}E${sign}${String(Math.abs(power)).padStart(2, '0')}`;
}

export function generalText(value: number, maxLength = GENERAL_LENGTH): string | null {
  if (value === 0) {
    return '0';
  }
  const abs = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const room = maxLength - sign.length;
  if (abs < 1e11 && abs >= 1e-9) {
    const intDigits = Math.max(1, Math.floor(Math.log10(abs)) + 1);
    if (intDigits <= room) {
      const decimals = Math.max(0, Math.min(room - intDigits - 1, 15));
      const text = trimFraction(abs.toFixed(decimals));
      if (Number(text) !== 0 || abs === 0) {
        return sign + text.replace('.', DECIMAL);
      }
    }
  }
  for (let decimals = 5; decimals >= 0; decimals--) {
    const text = exponentText(abs, decimals);
    if (text.length <= room) {
      return sign + text;
    }
  }
  return null;
}

function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, GROUP);
}

function bestFraction(value: number, maxDenominator: number): [number, number] {
  let p0 = 0;
  let q0 = 1;
  let p1 = 1;
  let q1 = 0;
  let rest = value;
  for (let step = 0; step < 64; step++) {
    const whole = Math.floor(rest);
    const q2 = q0 + whole * q1;
    if (q2 > maxDenominator) {
      break;
    }
    [p0, q0, p1, q1] = [p1, q1, p0 + whole * p1, q2];
    const fraction = rest - whole;
    if (fraction < 1e-12) {
      break;
    }
    rest = 1 / fraction;
  }
  const k = Math.floor((maxDenominator - q0) / q1);
  const near: [number, number] = [p0 + k * p1, q0 + k * q1];
  const best: [number, number] = [p1, q1];
  return Math.abs(value - near[0] / near[1]) < Math.abs(value - best[0] / best[1]) ? near : best;
}

function placeholder(slot: string): string {
  if (slot === '0') {
    return '0';
  }
  if (slot === '?') {
    return ' ';
  }
  return '';
}

function formatFraction(abs: number, tokens: Token[]): string {
  const slash = tokens.findIndex((token) => token.t === 'slash');
  let numeratorStart = slash;
  while (numeratorStart > 0 && tokens[numeratorStart - 1].t === 'digit') {
    numeratorStart--;
  }
  const integerSlots = tokens.slice(0, numeratorStart).filter((token) => token.t === 'digit').length;
  const numeratorSlots = slash - numeratorStart;

  let denominatorEnd = slash + 1;
  let fixedDenominator = '';
  let denominatorSlots = 0;
  while (denominatorEnd < tokens.length) {
    const token = tokens[denominatorEnd];
    if (token.t === 'digit') {
      denominatorSlots++;
    } else if (token.t === 'lit' && /^\d$/.test(token.v) && !denominatorSlots) {
      fixedDenominator += token.v;
    } else {
      break;
    }
    denominatorEnd++;
  }

  let whole = integerSlots ? Math.floor(abs) : 0;
  const fraction = abs - whole;
  let numerator: number;
  let denominator: number;
  if (fixedDenominator) {
    denominator = Number(fixedDenominator);
    numerator = Math.round(fraction * denominator);
  } else {
    [numerator, denominator] = bestFraction(fraction, 10 ** Math.min(Math.max(denominatorSlots, 1), 6) - 1);
  }
  if (integerSlots && numerator === denominator) {
    whole += 1;
    numerator = 0;
  }

  let output = '';
  let integerDone = false;
  tokens.forEach((token, index) => {
    if (index < numeratorStart) {
      if (token.t === 'digit') {
        if (!integerDone) {
          output += whole || numerator === 0 ? String(whole) : '';
        }
        integerDone = true;
      } else if (token.t === 'lit') {
        output += numerator === 0 || whole || index > 0 ? token.v : '';
      }
      return;
    }
    if (index === numeratorStart) {
      if (numerator === 0 && integerSlots) {
        output += ' '.repeat(numeratorSlots + 1 + Math.max(denominatorSlots, fixedDenominator.length));
      } else {
        output += String(numerator).padStart(numeratorSlots, ' ');
        output += '/';
        output += fixedDenominator || String(denominator).padEnd(denominatorSlots, ' ');
      }
      return;
    }
    if (index < denominatorEnd) {
      return;
    }
    if (token.t === 'lit') {
      output += token.v;
    } else if (token.t === 'fill') {
      output += FILL_MARK + token.v;
    } else if (token.t === 'percent') {
      output += '%';
    }
  });
  return output;
}

function formatNumberSection(value: number, section: Section, signed: boolean): string {
  const tokens = section.tokens;
  let abs = Math.abs(value);
  const percents = tokens.filter((token) => token.t === 'percent').length;
  abs *= 100 ** percents;

  if (tokens.some((token) => token.t === 'slash') && tokens.some((token) => token.t === 'digit')) {
    const text = formatFraction(abs, tokens);
    return signed && /[1-9]/.test(text) ? `-${text}` : text;
  }

  const pointIndex = tokens.findIndex((token) => token.t === 'point');
  const expIndex = tokens.findIndex((token) => token.t === 'exp');
  const integerEnd = pointIndex >= 0 ? pointIndex : expIndex >= 0 ? expIndex : tokens.length;
  const decimalEnd = expIndex >= 0 ? expIndex : tokens.length;

  let grouping = false;
  let scale = 0;
  tokens.forEach((token, index) => {
    if (token.t !== 'comma') {
      return;
    }
    const before = tokens.slice(0, index).some((other) => other.t === 'digit');
    const next = tokens.slice(index + 1).find((other) => other.t !== 'comma');
    if (before && next?.t === 'digit' && index < integerEnd) {
      grouping = true;
    } else if (before) {
      scale++;
    }
  });
  abs /= 1000 ** scale;

  const integerSlots: number[] = [];
  const decimalSlots: number[] = [];
  const exponentSlots: number[] = [];
  tokens.forEach((token, index) => {
    if (token.t !== 'digit') {
      return;
    }
    if (index < integerEnd) {
      integerSlots.push(index);
    } else if (index < decimalEnd) {
      decimalSlots.push(index);
    } else {
      exponentSlots.push(index);
    }
  });

  let exponent = 0;
  if (expIndex >= 0 && abs !== 0) {
    const width = Math.max(integerSlots.length, 1);
    exponent = Math.floor(Math.log10(abs));
    const engineering = width > 1 && integerSlots.some((index) => (tokens[index] as { v: string }).v === '#');
    exponent = engineering ? Math.floor(exponent / width) * width : exponent - (width - 1);
    abs /= 10 ** exponent;
    if (Number(fixedDecimal(abs, decimalSlots.length)) >= 10 ** width) {
      abs /= 10 ** (engineering ? width : 1);
      exponent += engineering ? width : 1;
    }
  }

  const fixed = fixedDecimal(abs, Math.min(decimalSlots.length, 100));
  const [integerPart, decimalPart = ''] = fixed.split('.');
  const integerDigits = integerPart === '0' ? '' : integerPart;
  const integerTokens = integerSlots.map((index) => (tokens[index] as { v: string }).v);

  const integerOutput = new Map<number, string>();
  if (grouping) {
    let text = integerDigits;
    const zeros = integerTokens.length - integerTokens.findIndex((slot) => slot === '0');
    if (integerTokens.includes('0')) {
      text = text.padStart(zeros, '0');
    }
    if (integerSlots.length) {
      integerOutput.set(integerSlots[0], groupDigits(text));
    }
    integerSlots.slice(1).forEach((index) => integerOutput.set(index, ''));
  } else {
    let remaining = integerDigits;
    for (let slot = integerSlots.length - 1; slot >= 0; slot--) {
      const index = integerSlots[slot];
      if (slot === 0) {
        integerOutput.set(index, remaining || placeholder(integerTokens[slot]));
      } else if (remaining) {
        integerOutput.set(index, remaining.slice(-1));
        remaining = remaining.slice(0, -1);
      } else {
        integerOutput.set(index, placeholder(integerTokens[slot]));
      }
    }
  }

  const decimalOutput = new Map<number, string>();
  decimalSlots.forEach((index, position) => {
    const slot = (tokens[index] as { v: string }).v;
    const digit = decimalPart[position] ?? '0';
    const restZero = /^0*$/.test(decimalPart.slice(position));
    if (slot === '0' || !restZero) {
      decimalOutput.set(index, digit);
    } else {
      decimalOutput.set(index, slot === '?' ? ' ' : '');
    }
  });

  let output = '';
  let exponentWritten = false;
  tokens.forEach((token, index) => {
    switch (token.t) {
      case 'digit':
        if (integerOutput.has(index)) {
          output += integerOutput.get(index);
        } else if (decimalOutput.has(index)) {
          output += decimalOutput.get(index);
        } else if (!exponentWritten) {
          exponentWritten = true;
          output += String(Math.abs(exponent)).padStart(exponentSlots.length, '0');
        }
        break;
      case 'point':
        if (index === pointIndex) {
          output += DECIMAL;
        }
        break;
      case 'exp':
        output += `E${exponent < 0 ? '-' : token.plus ? '+' : ''}`;
        break;
      case 'percent':
        output += '%';
        break;
      case 'lit':
        output += token.v;
        break;
      case 'fill':
        output += FILL_MARK + token.v;
        break;
      case 'general':
        output += generalText(abs) ?? '';
        break;
    }
  });

  const isZero = !/[1-9]/.test(fixed) && !(expIndex >= 0 && value !== 0);
  return signed && !isZero ? `-${output}` : output;
}

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function formatDateSection(serial: number, section: Section, date1904: boolean): string {
  if (!(serial >= 0 && serial < LAST_DATE_SERIAL)) {
    return '#'.repeat(10);
  }
  const subsecond = Math.max(0, ...section.tokens.map((token) => (token.t === 'subsec' ? token.n : 0)));
  const precision = 10 ** subsecond;
  const totalUnits = Math.round(serial * 86400 * precision);
  const dayNumber = Math.floor(totalUnits / (86400 * precision));
  const dayUnits = totalUnits - dayNumber * 86400 * precision;
  const seconds = Math.floor(dayUnits / precision);
  const fraction = dayUnits - seconds * precision;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  let year: number;
  let month: number;
  let day: number;
  let weekday: number;
  if (!date1904 && dayNumber === 60) {
    year = 1900;
    month = 1;
    day = 29;
    weekday = 3;
  } else {
    const offset = date1904 ? dayNumber + 1462 : dayNumber > 60 ? dayNumber - 1 : dayNumber;
    const date = new Date(Date.UTC(1899, 11, 31) + offset * 86400000);
    year = date.getUTCFullYear();
    month = date.getUTCMonth();
    day = date.getUTCDate();
    weekday = date.getUTCDay();
  }

  const totalSeconds = Math.floor(totalUnits / precision);
  const twelveHour = section.tokens.some((token) => token.t === 'ampm');
  const months = section.english ? MONTHS_EN : section.genitive ? MONTHS_GENITIVE : MONTHS;
  const days = section.english ? DAYS_EN : DAYS;

  let output = '';
  section.tokens.forEach((token) => {
    switch (token.t) {
      case 'lit':
        output += token.v;
        break;
      case 'fill':
        output += FILL_MARK + token.v;
        break;
      case 'subsec':
        output += DECIMAL + pad(fraction, subsecond).slice(0, token.n);
        break;
      case 'ampm':
        output += token.v === 'A/P' ? (hours < 12 ? 'A' : 'P') : hours < 12 ? 'AM' : 'PM';
        break;
      case 'elapsed':
        if (token.v === 'h') {
          output += pad(Math.floor(totalSeconds / 3600), 1);
        } else if (token.v === 'm') {
          output += pad(Math.floor(totalSeconds / 60), 1);
        } else {
          output += pad(totalSeconds, 1);
        }
        break;
      case 'date': {
        const length = token.v.length;
        switch (token.v[0]) {
          case 'y':
            output += length <= 2 ? pad(year % 100) : String(year);
            break;
          case 'm':
            if (length === 1) {
              output += String(month + 1);
            } else if (length === 2) {
              output += pad(month + 1);
            } else if (length === 3) {
              output += section.english ? MONTHS_EN[month].slice(0, 3) : MONTHS_SHORT[month];
            } else if (length === 5) {
              output += months[month][0];
            } else {
              output += months[month];
            }
            break;
          case 'n':
            output += length === 1 ? String(minutes) : pad(minutes);
            break;
          case 'd':
            if (length === 1) {
              output += String(day);
            } else if (length === 2) {
              output += pad(day);
            } else if (length === 3) {
              output += section.english ? DAYS_EN[weekday].slice(0, 3) : DAYS_SHORT[weekday];
            } else {
              output += days[weekday];
            }
            break;
          case 'h': {
            const value = twelveHour ? hours % 12 || 12 : hours;
            output += length === 1 ? String(value) : pad(value);
            break;
          }
          case 's':
            output += length === 1 ? String(secs) : pad(secs);
            break;
        }
        break;
      }
      case 'digit':
        output += token.v === '0' ? '0' : '';
        break;
      case 'point':
        output += '.';
        break;
      case 'comma':
        output += ',';
        break;
      case 'slash':
        output += '/';
        break;
      case 'percent':
        output += '%';
        break;
      default:
        break;
    }
  });
  return output;
}

function finish(text: string, color?: string): FormattedValue {
  const mark = text.indexOf(FILL_MARK);
  if (mark < 0) {
    return { text, color };
  }
  const before = text.slice(0, mark);
  const after = text
    .slice(mark + 2)
    .split(FILL_MARK)
    .join('');
  return { text: before + after, color, fill: { before, after } };
}

export function formatNumber(value: number, code?: string, date1904 = false): FormattedValue {
  if (!code || /^general$/i.test(code.trim())) {
    return { text: generalText(value) ?? '' };
  }
  const sections = compile(code);
  const { section, signed } = pickSection(sections, value);
  if (!section) {
    return { text: generalText(value) ?? '' };
  }
  if (section.isDate) {
    return finish(formatDateSection(value, section, date1904), section.color);
  }
  if (!section.hasDigits && section.hasText) {
    return { text: generalText(value) ?? '' };
  }
  if (!section.hasDigits) {
    const literal = section.tokens
      .map((token) => (token.t === 'lit' ? token.v : token.t === 'fill' ? FILL_MARK + token.v : ''))
      .join('');
    return finish(signed ? `-${literal}` : literal, section.color);
  }
  return finish(formatNumberSection(value, section, signed), section.color);
}

export function formatText(text: string, code?: string): FormattedValue {
  if (!code) {
    return { text };
  }
  const sections = compile(code);
  const section = sections[3] ?? (sections.length === 1 && sections[0].hasText ? sections[0] : undefined);
  if (!section) {
    return { text };
  }
  const output = section.tokens
    .map((token) => {
      if (token.t === 'text') {
        return text;
      }
      if (token.t === 'lit') {
        return token.v;
      }
      if (token.t === 'fill') {
        return FILL_MARK + token.v;
      }
      return '';
    })
    .join('');
  return finish(output, section.color);
}

export function isGeneralFormat(code?: string): boolean {
  return !code || /^general$/i.test(code.trim());
}

export function columnCharsToPx(width: number, digitWidth: number): number {
  return Math.trunc(((256 * width + Math.trunc(128 / digitWidth)) / 256) * digitWidth);
}

export function defaultColumnPx(baseWidth: number, digitWidth: number): number {
  const px = columnCharsToPx(baseWidth, digitWidth) + 5;
  return Math.ceil(px / 8) * 8;
}
